import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
/**
 * Reusable login form fields.
 * Used by both the full LoginPage and the SessionExpiredModal.
 *
 * When `onSubmit` is provided, the form submits via JS (AJAX).
 * When omitted, the form falls back to a traditional POST to /users/login.
 */
const LoginForm = ({ email: initialEmail = '', error: externalError, success, onSubmit, submitLabel = 'Login', loading = false, }) => {
    const [email, setEmail] = useState(initialEmail);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [internalError, setInternalError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const error = externalError || internalError;
    const busy = loading || isSubmitting;
    // Traditional POST fallback URL (preserves query params like ?redir=)
    const currentUrl = new URL(window.location.href);
    const searchParams = currentUrl.searchParams.toString();
    const formAction = searchParams ? `/users/login?${searchParams}` : '/users/login';
    const handleSubmit = async (e) => {
        if (!onSubmit)
            return; // Let the browser handle the traditional POST
        e.preventDefault();
        setInternalError(null);
        setIsSubmitting(true);
        try {
            await onSubmit(email, password);
        }
        catch (err) {
            setInternalError(err?.message || 'Login failed');
        }
        finally {
            setIsSubmitting(false);
        }
    };
    return (_jsx("form", { method: onSubmit ? undefined : 'POST', action: onSubmit ? undefined : formAction, onSubmit: handleSubmit, children: _jsxs("div", { className: "space-y-4", children: [error && (_jsx(Alert, { variant: "destructive", children: _jsx(AlertDescription, { children: error }) })), success && (_jsx(Alert, { className: "border-green-500/50 bg-green-500/10 text-green-600 dark:text-green-400", children: _jsx(AlertDescription, { children: success }) })), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "login-email", children: "Email" }), _jsx(Input, { id: "login-email", name: "email", type: "email", placeholder: "Enter your email", value: email, onChange: (e) => setEmail(e.target.value), required: true, autoComplete: "email", disabled: busy })] }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "login-password", children: "Password" }), _jsxs("div", { className: "relative", children: [_jsx(Input, { id: "login-password", name: "password", type: showPassword ? 'text' : 'password', placeholder: "Enter your password", value: password, onChange: (e) => setPassword(e.target.value), required: true, autoComplete: "current-password", className: "pr-10", disabled: busy }), _jsx("button", { type: "button", onClick: () => setShowPassword(!showPassword), className: "absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground", tabIndex: -1, children: showPassword ? (_jsx(EyeOff, { className: "h-4 w-4" })) : (_jsx(Eye, { className: "h-4 w-4" })) })] })] }), _jsx(Button, { type: "submit", className: "w-full", disabled: busy, children: busy ? 'Logging in...' : submitLabel })] }) }));
};
export default LoginForm;
